'use client';

import { motion } from 'framer-motion';
import { BRAND_COLORS, ROUTES } from '@/constants';
import type { LocalizedComponentProps } from '@/types';

const Footer = ({ dict }: LocalizedComponentProps) => {
  const quickLinks = [
    { name: "Home", href: ROUTES.HOME },
    { name: "About Us", href: ROUTES.ABOUT },
    { name: "Find Events", href: ROUTES.EVENTS },
    { name: "Find Services", href: ROUTES.SERVICES }
  ];

  const socialLinks = [
    { name: "Facebook", href: "#", icon: "facebook" },
    { name: "Twitter", href: "#", icon: "twitter" },
    { name: "YouTube", href: "#", icon: "youtube" }
  ];

  const getSocialIcon = (iconName: string) => {
    const icons = {
      youtube: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
      facebook: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
      twitter: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
        </svg>
      )
    };
    return icons[iconName as keyof typeof icons] || null;
  };

  return (
    <footer 
      className="py-16"
      style={{ backgroundColor: BRAND_COLORS.DARK_BLUE }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* About Us Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="mb-4">
              <img 
                src="/images/realImages/pointters.png" 
                alt="Pointters Community Initiatives" 
                className="h-12 w-auto"
              />
            </div>
            <p 
              className="text-body-md leading-relaxed mb-4"
              style={{ color: '#FFFFFF' }}
            >
              Pointters Community Initiatives is a 501(c)(3) nonprofit organization dedicated to creating opportunities and supporting individuals in the Fox Valley and beyond. We focus on providing resources, fostering growth, and driving meaningful change to help people build better futures.
            </p>
          </motion.div>

          {/* Sign Up for E-News Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 
              className="text-heading-lg font-semibold mb-4"
              style={{ color: '#FFFFFF' }}
            >
              Sign Up for E-News
            </h4>
            <p 
              className="text-body-md leading-relaxed mb-4"
              style={{ color: '#FFFFFF' }}
            >
              Stay informed through our e-newsletters – timely and relevant information delivered right to your inbox.
            </p>
            <button 
              className="px-6 py-3 border-2 border-white font-medium rounded-full hover:bg-white hover:text-[#203F72] transition-all duration-200"
              style={{ color: '#FFFFFF' }}
            >
              Sign Up Here
            </button>
          </motion.div>

          {/* Connect With Us Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 
              className="text-heading-lg font-semibold mb-4"
              style={{ color: '#FFFFFF' }}
            >
              Connect With Us
            </h4>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                <span className="text-body-md" style={{ color: '#FFFFFF' }}>(920)-509-2270</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                <span className="text-body-md" style={{ color: '#FFFFFF' }}>Info@pointters.org</span>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                <span className="text-body-md" style={{ color: '#FFFFFF' }}>1800 Appleton Rd Menasha</span>
              </div>
            </div>
            <div className="flex space-x-4 mt-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#203F72] hover:bg-gray-200 transition-colors duration-200"
                  aria-label={social.name}
                >
                  {getSocialIcon(social.icon)}
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h4 
              className="text-heading-lg font-semibold mb-4"
              style={{ color: '#FFFFFF' }}
            >
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-body-md transition-colors duration-200 hover:text-gray-300"
                    style={{ color: '#FFFFFF' }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* 988 Suicide & Crisis Lifeline Section */}
        <motion.div
          className="border-t border-gray-400 pt-8 mb-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex justify-end">
            <div className="flex items-center space-x-4">
              <div className="text-4xl font-bold" style={{ color: '#FFFFFF' }}>988</div>
              <div style={{ color: '#FFFFFF' }}>
                <div className="text-sm font-semibold">SUICIDE & CRISIS</div>
                <div className="text-sm font-semibold">LIFELINE</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-gray-400 pt-8 flex flex-col md:flex-row justify-between items-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="flex space-x-6 mb-4 md:mb-0">
            <a
              href="#"
              className="text-body-md transition-colors duration-200 hover:text-gray-300"
              style={{ color: '#FFFFFF' }}
            >
              Terms of Service
            </a>
            <a
              href="#"
              className="text-body-md transition-colors duration-200 hover:text-gray-300"
              style={{ color: '#FFFFFF' }}
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-body-md transition-colors duration-200 hover:text-gray-300"
              style={{ color: '#FFFFFF' }}
            >
              Cookies
            </a>
          </div>
          <p 
            className="text-body-md"
            style={{ color: '#FFFFFF' }}
          >
            Pointters @ 2025. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
